/**
 * Every persisted setting this port has, as one value — the model behind the
 * PREFERENCES panel (`shell/model/preferences-screen.ts`).
 *
 * ## It is a *view*, not a new store
 *
 * The five groups underneath keep their own `localStorage` keys, their own
 * defaults and their own normalizers: `audio/settings.ts`,
 * `flight/cockpit-settings.ts`, `flight/effects-settings.ts`,
 * `flight/stick-settings.ts` and `mission/realism-settings.ts`. Nothing is
 * copied into a sixth key and nothing is rewritten into a new shape, which is
 * what makes the consolidation **lossless by construction**: a player who set
 * his volumes in 2026-08-19's popover, his SKY chip in either of the two shapes
 * that control has had, and his Realism Panel last week opens this screen on
 * exactly those values, because it is reading the same bytes the same code
 * always read. `effects-settings.ts`'s `storedEnhancedSky` — which folds all
 * three eras of that key into today's boolean — still runs on the way in, so a
 * payload from any of them lands on the picture it always named.
 *
 * ## The one field that lives in two places
 *
 * `REALISTIC INSTRUMENTS` is `CockpitSettings.realisticInstruments` *and*
 * `RealismPanel.extra.realisticInstruments`; `saveRealism` already writes
 * through to the cockpit's store so the two can never disagree on disk. This
 * module keeps that invariant in memory too (`syncInstruments`), so a click on
 * the PREFERENCES panel's checkbox and a click on the Realism Panel's are the
 * same click.
 */

import {
  clampVolume,
  DEFAULT_AUDIO_SETTINGS,
  loadAudioSettings,
  saveAudioSettings,
  type AudioSettings,
  type SettingsStore,
} from "../audio/settings.ts";
import { DEFAULT_COCKPIT_SETTINGS, saveCockpitSettings, type CockpitSettings } from "../flight/cockpit-settings.ts";
import {
  DEFAULT_EFFECTS_SETTINGS,
  loadEffectsSettings,
  saveEffectsSettings,
  type EffectsSettings,
} from "../flight/effects-settings.ts";
import {
  DEFAULT_BAIL_SETTINGS,
  loadBailSettings,
  saveBailSettings,
  type BailSettings,
} from "../flight/bail-settings.ts";
import {
  DEFAULT_SIM_SETTINGS,
  loadSimSettings,
  saveSimSettings,
  type SimSettings,
} from "../flight/sim-settings.ts";
import {
  DEFAULT_STICK_SETTINGS,
  loadStickSettings,
  saveStickSettings,
  type StickSettings,
} from "../flight/stick-settings.ts";
import { applyRealismCommand, currentRealism, DEFAULT_REALISM, saveRealism, type RealismPanel } from "./realism-settings.ts";

export interface Preferences {
  readonly audio: AudioSettings;
  readonly cockpit: CockpitSettings;
  readonly effects: EffectsSettings;
  readonly stick: StickSettings;
  readonly realism: RealismPanel;
  /** AD-021's bail-out verb. A port enhancement — see `flight/bail-settings.ts`. */
  readonly bail: BailSettings;
  /**
   * ENHANCED SIMULATION — the only setting here that changes what the *sim*
   * does rather than what it looks like, and the only one that is off by
   * default. See `flight/sim-settings.ts` for both arguments.
   */
  readonly simulation: SimSettings;
}

export const DEFAULT_PREFERENCES: Preferences = {
  audio: DEFAULT_AUDIO_SETTINGS,
  cockpit: DEFAULT_COCKPIT_SETTINGS,
  effects: DEFAULT_EFFECTS_SETTINGS,
  stick: DEFAULT_STICK_SETTINGS,
  realism: DEFAULT_REALISM,
  bail: DEFAULT_BAIL_SETTINGS,
  simulation: DEFAULT_SIM_SETTINGS,
};

/** The volume buses the panel steps, in the order it prints them. */
export const VOLUME_BUSES = ["master", "music", "sfx"] as const;
export type VolumeBus = (typeof VOLUME_BUSES)[number];

export const VOLUME_BUS_LABELS: Readonly<Record<VolumeBus, string>> = {
  master: "MASTER",
  music: "MUSIC",
  sfx: "EFFECTS",
};

/**
 * One press of a `-`/`+` chip. A tenth of full travel: eleven stops is what a
 * two-button field can be walked end to end in without becoming a chore, and it
 * prints as a round percentage in the well beside it.
 */
export const VOLUME_STEP = 0.1;

/**
 * The `EffectsSettings` members the panel prints, in panel order — **all of
 * them**, since the SKY selector collapsed into `enhancedSky` and DISPLAY became
 * seven checkboxes with nothing else in it. `EffectsSettings` having no member
 * that is not a boolean is what lets this be one list rather than a list plus a
 * special case.
 *
 * `cockpit3d` is last because it is the newest of the seven, and it sits
 * directly under the row it depends on in spirit if not in order — see
 * `EFFECT_TOGGLE_DEPENDS_ON`.
 */
export const EFFECT_TOGGLE_IDS = [
  "propellerEffect",
  "killCamera",
  "enhancedEffects",
  "enhancedModels",
  "enhancedTerrain",
  "enhancedSky",
  "cockpit3d",
] as const;

export type EffectToggleId = (typeof EFFECT_TOGGLE_IDS)[number];

/**
 * Verbatim on the panel's chips, in the port's own voice — none of these is one
 * of the original's options.
 *
 * `cockpit3d` reads plain `3D COCKPIT`, not the design note's original
 * `3D COCKPIT (EXPERIMENTAL)` or this panel's own former `EXPERIMENTAL 3D
 * COCKPIT`: as of 2026-08-26 the row is on by default alongside the rest of
 * DISPLAY (`effects-settings.ts`), and a default-on row calling itself
 * experimental would be lying to every pilot who never touched the panel.
 */
export const EFFECT_TOGGLE_LABELS: Readonly<Record<EffectToggleId, string>> = {
  propellerEffect: "PROPELLER EFFECT",
  killCamera: "KILL CAMERA",
  enhancedEffects: "ENHANCED EFFECTS",
  enhancedModels: "ENHANCED MODELS",
  enhancedTerrain: "ENHANCED TERRAIN",
  enhancedSky: "ENHANCED SKY",
  cockpit3d: "3D COCKPIT",
};

/**
 * What a row says when the thing it switches is not there to switch.
 *
 * The precedent and the argument are `EffectsToggles.tsx`'s
 * `ENHANCED_MODELS_UNAVAILABLE_LABEL`: a control that cannot change the picture
 * has to say so, or a pilot clicks it, sees nothing happen, and has no way to
 * tell a broken feature from a missing one. The setting is still written either
 * way, so turning it off here has it off when the models do arrive.
 */
export const EFFECT_TOGGLE_UNAVAILABLE_LABELS: Partial<Readonly<Record<EffectToggleId, string>>> = {
  cockpit3d: "3D COCKPIT: UNAVAILABLE",
};

/**
 * Rows that only do anything while another row is on.
 *
 * One entry, and it is `cockpit3d` on `enhancedModels` — the 3D cockpit draws
 * the enhanced `.glb` and nothing else can stand in for it (`effects-settings.ts`
 * says why at length). Expressed as data rather than as an `if` in the screen
 * builder so that the *reason* a row is greyed is the same fact the scene reads
 * when it decides not to draw.
 */
export const EFFECT_TOGGLE_DEPENDS_ON: Partial<Readonly<Record<EffectToggleId, EffectToggleId>>> = {
  cockpit3d: "enhancedModels",
};

/** `0.8` → `80%`, the only form the panel prints a volume in. */
export function volumePercent(value: number): string {
  return `${Math.round(clampVolume(value) * 100)}%`;
}

/**
 * Keeps the cockpit's copy of REALISTIC INSTRUMENTS and the Realism Panel's the
 * same bit. Returns the value it was given when they already agree, so
 * `dispatchPreferences`' identity test stays honest.
 */
function syncInstruments(prefs: Preferences, realisticInstruments: boolean): Preferences {
  const agreed =
    prefs.cockpit.realisticInstruments === realisticInstruments &&
    prefs.realism.extra.realisticInstruments === realisticInstruments;
  if (agreed) return prefs;
  return {
    ...prefs,
    cockpit: { ...prefs.cockpit, realisticInstruments },
    realism: { ...prefs.realism, extra: { ...prefs.realism.extra, realisticInstruments } },
  };
}

/** Everything currently on disk, or the defaults where the browser has no storage. */
export function loadPreferences(store: SettingsStore | undefined): Preferences {
  if (!store) return DEFAULT_PREFERENCES;
  const realism = currentRealism(store);
  return {
    audio: loadAudioSettings(store),
    // Read from the Realism Panel's own load, which already reads it out of the
    // cockpit store — one path in, so the two copies start out agreeing.
    cockpit: { realisticInstruments: realism.extra.realisticInstruments },
    effects: loadEffectsSettings(store),
    stick: loadStickSettings(store),
    realism,
    bail: loadBailSettings(store),
    simulation: loadSimSettings(store),
  };
}

/**
 * Where a changed group goes. The flying routes pass their own live-apply
 * functions (`flight/live-settings.ts`) so a change reaches the running scene
 * as well as the disk; the shell passes `storePreferenceSinks`, which only
 * writes.
 */
export interface PreferenceSinks {
  audio(next: AudioSettings): void;
  cockpit(next: CockpitSettings): void;
  effects(next: EffectsSettings): void;
  stick(next: StickSettings): void;
  realism(next: RealismPanel): void;
  bail(next: BailSettings): void;
  simulation(next: SimSettings): void;
}

export function storePreferenceSinks(store: SettingsStore | undefined): PreferenceSinks {
  return {
    audio(next) {
      if (store) saveAudioSettings(store, next);
    },
    cockpit(next) {
      if (store) saveCockpitSettings(store, next);
    },
    effects(next) {
      if (store) saveEffectsSettings(store, next);
    },
    stick(next) {
      if (store) saveStickSettings(store, next);
    },
    realism(next) {
      if (store) saveRealism(store, next);
    },
    bail(next) {
      if (store) saveBailSettings(store, next);
    },
    simulation(next) {
      if (store) saveSimSettings(store, next);
    },
  };
}

/**
 * Sends each group that actually changed to its own sink, and nothing else.
 * Compared by identity: every reducer below rebuilds only the group it touched,
 * so an untouched group is the same object and its sink is not called —
 * which is what stops a volume step from rewriting the Realism Panel.
 */
export function dispatchPreferences(previous: Preferences, next: Preferences, sinks: PreferenceSinks): void {
  if (next.audio !== previous.audio) sinks.audio(next.audio);
  if (next.cockpit !== previous.cockpit) sinks.cockpit(next.cockpit);
  if (next.effects !== previous.effects) sinks.effects(next.effects);
  if (next.stick !== previous.stick) sinks.stick(next.stick);
  if (next.realism !== previous.realism) sinks.realism(next.realism);
  if (next.bail !== previous.bail) sinks.bail(next.bail);
  if (next.simulation !== previous.simulation) sinks.simulation(next.simulation);
}

function volumeCommand(prefs: Preferences, rest: string): Preferences | undefined {
  const [name, direction] = rest.split(":");
  const bus = VOLUME_BUSES.find((candidate) => candidate === name);
  if (!bus || (direction !== "up" && direction !== "down")) return undefined;
  const step = direction === "up" ? VOLUME_STEP : -VOLUME_STEP;
  // Quantised to a whole percent, because a percent is what the well prints and
  // what the store then carries: `0.7 - 0.1` is `0.6000000000000001` in binary
  // floating point, and a lever walked up and down for a while should come back
  // to the number it started on rather than to a drifting neighbour of it.
  const stepped = Math.round(clampVolume(prefs.audio[bus] + step) * 100) / 100;
  return { ...prefs, audio: { ...prefs.audio, [bus]: stepped } };
}

function effectCommand(prefs: Preferences, id: string): Preferences | undefined {
  const key = EFFECT_TOGGLE_IDS.find((candidate) => candidate === id);
  if (!key) return undefined;
  return { ...prefs, effects: { ...prefs.effects, [key]: !prefs.effects[key] } };
}

/**
 * The panel's own commands. Returns `undefined` for one it does not own, so a
 * surface can offer every command here first and fall through to its own
 * navigation — the same contract `applyRealismCommand` has, which this
 * delegates to for the Realism Panel it opens.
 */
export function applyPreferencesCommand(prefs: Preferences, command: string, restore: Preferences): Preferences | undefined {
  if (command.startsWith("pref:volume:")) return volumeCommand(prefs, command.slice("pref:volume:".length));
  if (command.startsWith("pref:effect:")) return effectCommand(prefs, command.slice("pref:effect:".length));
  if (command === "pref:mute") return { ...prefs, audio: { ...prefs.audio, muted: !prefs.audio.muted } };
  if (command === "pref:stick") {
    return { ...prefs, stick: { shaping: prefs.stick.shaping === "authentic" ? "instant" : "authentic" } };
  }
  if (command === "pref:instruments") return syncInstruments(prefs, !prefs.cockpit.realisticInstruments);
  if (command === "pref:bail") return { ...prefs, bail: { enabled: !prefs.bail.enabled } };
  if (command === "pref:simulation") {
    return { ...prefs, simulation: { enhancedSimulation: !prefs.simulation.enhancedSimulation } };
  }
  if (command === "pref:restore") return restore;
  const realism = applyRealismCommand(prefs.realism, command, restore.realism);
  // The Realism Panel owns REALISTIC INSTRUMENTS too (its `extra`), so a preset
  // or a click on its own checkbox has to reach the cockpit's copy as well.
  return realism ? syncInstruments({ ...prefs, realism }, realism.extra.realisticInstruments) : undefined;
}
